<template>
    <div :class="{'pointer-events-none': disabled}">
        <div class="row">
            <div class="col-full lg:col-1/2">
                <FormGroup
                    :name="`${this.name}.start`"
                    label="Start Date">
                    <div class="flex">
                        <ElDatePicker
                            name="start_date"
                            format="DD/MM/YYYY"
                            placeholder="Select Date"
                            :clearable="false"
                            :disabledDate="disabledStartDate"
                            v-model="start.date"/>

                        <ElTimeSelect
                            name="start_time"
                            format="HH:mm"
                            start="06:00"
                            end="21:00"
                            step="0:15"
                            placeholder="Select time"
                            :clearable="false"
                            v-model="start.time"/>
                    </div>
                </FormGroup>
            </div>
            <div class="col-full lg:col-1/2">

                <FormGroup
                    :name="`${this.name}.end`"
                    label="End date">
                    <div class="flex">
                        <ElDatePicker
                            name="end_date"
                            format="DD/MM/YYYY"
                            placeholder="Select Date"
                            :clearable="false"
                            :disabledDate="disabledEndDate"
                            v-model="end.date"/>

                        <ElTimeSelect
                            name="end_time"
                            format="HH:mm"
                            start="06:00"
                            end="21:00"
                            step="0:15"
                            placeholder="Select time"
                            :clearable="false"
                            v-model="end.time"/>
                    </div>
                </FormGroup>
            </div>
        </div>

        <FormGroup
            :name="`${this.name}.type`"
            label="Type">
            <Select
                v-model="type"
                :name="type"
                :options="types"
                :disabled="disabled"
                placeholder="Please enter the type..."/>
        </FormGroup>

        <FormGroup
            v-if="type === 'Zoom' || courseType == 2"
            :name="`${this.name}.location_url`"
            label="Link">
            <Input
                v-model="location_url"
                name="location_url"
                placeholder="Please enter the location url..."
                :readonly="disabled"/>
        </FormGroup>

        <FormGroup
            :name="`${this.name}.location_notes`"
            label="Notes/Details">
            <Textarea
                v-model="location_notes"
                name="location_notes"
                placeholder="Please enter the location notes..."
                :readonly="disabled"/>
        </FormGroup>
    </div>
</template>

<script>

import FormSection from "@/Components/Form/FormSection.vue";
import FormGroup from "@/Components/Form/FormGroup.vue";
import Input from "@/Components/Form/Input.vue";
import Select from "@/Components/Form/Select.vue";
import File from "@/Components/Form/File.vue";
import Textarea from "@/Components/Form/Textarea.vue";
import InputError from "@/Components/Form/InputError.vue";
import Label from "@/Components/Form/Label.vue";
import Checkbox from "@/Components/Form/Checkbox.vue";
import {ElDatePicker, ElTimeSelect} from "element-plus";
import dayjs from "dayjs";
import {get, set} from "lodash";

export default {

    name: "CourseSessionForm",

    props: {
        name: {
            type: String,
            required: true
        },
        session: {
            type: Object,
            required: true
        },
        disabled: {
            type: Boolean,
            default: () => false
        },
        courseType: {
            type: Number,
        }
    },

    emits: ['submit'],

    data() {
        return {
            types: [
                {label: "Zoom", value: "Zoom"},
                {label: "Face-to-face", value: "F2F"},
                {label: "E-Learning", value: "Elearning"},
            ],
            start: {
                date: null,
                time: null
            },
            end: {
                date: null,
                time: null
            }
        };
    },

    inject: ['form'],

    mounted() {
        this.setStartDate();
        this.setEndDate();
    },

    components: {
        FormSection,
        FormGroup,
        Input,
        Select,
        File,
        Textarea,
        Label,
        InputError,
        Checkbox,
        ElDatePicker,
        ElTimeSelect
    },

    methods: {
        setStartDate() {
            const datetime = dayjs(this.session.start);

            this.start.date = datetime.isValid() ? datetime.format('YYYY-MM-DD') : null;
            this.start.time = datetime.isValid() ? datetime.format('HH:mm') : null;
        },
        setEndDate() {
            const datetime = dayjs(this.session.end);

            this.end.date = datetime.isValid() ? datetime.format('YYYY-MM-DD') : null;
            this.end.time = datetime.isValid() ? datetime.format('HH:mm') : null;
        },
        disabledStartDate(date) {
            return dayjs(date).isBefore(dayjs().startOf('day'));
        },
        disabledEndDate(date) {
            return dayjs(date).isBefore(dayjs().startOf('day'));
        },
    },

    computed: {
        type: {
            get() {
                return get(this.form, `${this.name}.type`);
            },
            set(value) {
                set(this.form, `${this.name}.type`, value)
            }
        },
        location_url: {
            get() {
                return get(this.form, `${this.name}.location_url`);
            },
            set(value) {
                set(this.form, `${this.name}.location_url`, value)
            }
        },
        location_notes: {
            get() {
                return get(this.form, `${this.name}.location_notes`);
            },
            set(value) {
                set(this.form, `${this.name}.location_notes`, value)
            }
        },
    },

    watch: {
        'session.type'() {
            this.form.errors.clear(`${this.name}.location_url`);
            this.form.errors.clear(`${this.name}.location_notes`);
        },
        'session.start'() {
            this.form.errors.clear(`${this.name}.start`);
            this.form.errors.clear(`${this.name}.end`);
            this.setStartDate();
        },
        'session.end'() {
            this.form.errors.clear(`${this.name}.start`);
            this.form.errors.clear(`${this.name}.end`);
            this.setEndDate();
        },
        start: {
            deep: true,
            handler(start) {

                let newStart = null;

                if (start.date && start.time) {
                    const date = dayjs(start.date);
                    const time = dayjs(start.time, 'HH:mm');

                    newStart = date
                        .hour(isNaN(time.hour()) ? 0 : time.hour())
                        .minute(isNaN(time.minute()) ? 0 : time.minute())
                        .second(0)
                        .toISOString();
                }

                set(this.form, `${this.name}.start`, newStart);
            }
        },
        end: {
            deep: true,
            handler(end) {

                let newEnd = null;

                if (end.date && end.time) {
                    const date = dayjs(end.date);
                    const time = dayjs(end.time, 'HH:mm');

                    newEnd = date
                        .hour(isNaN(time.hour()) ? 0 : time.hour())
                        .minute(isNaN(time.minute()) ? 0 : time.minute())
                        .second(0)
                        .toISOString();
                }

                set(this.form, `${this.name}.end`, newEnd);
            }
        },
    }
};

</script>
